import {AsyncPipe} from '@angular/common';
import {
    AfterViewChecked,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {MatProgressSpinner} from "@angular/material/progress-spinner";

import {ImageAsset} from '@app/asset';
import {PresentingState, Slot} from "@app/review/models";
import {IResizable} from '@app/review/resizable';
import {ReviewImageService} from '@app/review/review-image.service';
import {ReviewService} from '@app/review/review.service';
import {Matrix, Point, Rect, Size} from '@app/shared/euclid';
import {ViewerTool} from '@app/viewer/viewable.interface';
import {ViewerToolsService} from '@app/viewer/viewer-tools.service';
import {MessageType} from '@app/websocket/websocket.service';

import {combineLatestWith, fromEvent, Observable, tap} from 'rxjs';
import {filter, map} from "rxjs/operators";


class ServiceStateVM {
    view: number[];
    presentingState: PresentingState;
}


@Component({
    selector: 'frog-review-image',
    imports: [AsyncPipe, MatProgressSpinner],
    templateUrl: './review-image.component.html',
    styleUrl: './review-image.component.scss',
})
export class ReviewImageComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked, IResizable {
    @Input() asset: ImageAsset;
    // Which half of the compare-parameterized ReviewImageService this instance reads/writes - see
    // docs/design/review-compare-assets.md §3.2.
    @Input() slot: Slot = Slot.Primary;
    // False for the passive pane while comparing - no pan/zoom handles, no bounds reporting (§3.4).
    @Input() isActive: boolean = true;
    @ViewChild('img') img: ElementRef;
    @ViewChild('canvas') canvas: ElementRef;

    public vm$: Observable<ServiceStateVM>;
    public isScaledFit: boolean;
    public bounds: Rect;
    public loading: boolean;

    private origin: Point;
    private xform: Matrix;
    private main: Matrix;
    // Last [zoom, panX, panY] view received from the sync channel (see review-image.service.ts).
    private lastView: number[];
    // Live PresentingState, mirrored from presenterState$ via the vm$ pipe.
    private presentingState: PresentingState = PresentingState.None;
    private ctx: CanvasRenderingContext2D;
    private isMouseDown: boolean;
    private tools: any;
    private _resizeObserver?: ResizeObserver;
    private _resizeTimeout?: ReturnType<typeof setTimeout>;
    // Set only by ngOnDestroy. The render() rAF loop checks this so a leaked
    // callback chain stops once the component is gone; nothing else halts the
    // loop while the component stays mounted (a same-type asset switch reuses
    // this instance and keeps rendering).
    private _destroyed: boolean = false;

    constructor(
        private reviewService: ReviewService,
        private reviewImageService: ReviewImageService,
        private viewerToolsService: ViewerToolsService,
        private host: ElementRef,
    ) {
        this.origin = new Point();
        this.xform = Matrix.Identity();
        this.main = Matrix.Identity();
        this.lastView = [1, 0, 0];
        this.bounds = new Rect();

        this.tools = {
            fitScreen: new ViewerTool({
                icon: 'fit_screen',
                tooltip: 'Zoom to fit window',
                action: () => this.scaleToWindow(),
            }),
            original: new ViewerTool({
                icon: 'zoom_in',
                tooltip: 'Zoom to original size',
                action: () => this.center(),
            }),
            // fullscreen: new ViewerTool({
            //     icon: 'branding_watermark_outline',
            //     tooltip: 'Fullscreen',
            //     action: () => this.openFullscreen()
            // })
        };
    }

    ngOnInit() {
        this.render();
        this.vm$ = this.reviewImageService.transform$
            .pipe(
                filter((slotted) => slotted.slot === this.slot),
                tap((slotted) => {
                    this.lastView = slotted.view;
                    this.applyTransform(slotted.view);
                }),
                combineLatestWith(this.reviewService.presenterState$),
                tap(([, presentingState]) => (this.presentingState = presentingState)),
                map(([slotted, presentingState]) => {
                    return {view: slotted.view, presentingState};
                })
            );

        this.emitTools();

        this._resizeObserver = new ResizeObserver(() => {
            // ResizeObserver can deliver synchronously mid-digest (e.g. off a
            // forced layout read elsewhere in the same tick), which races
            // Angular's checkNoChanges pass and throws NG0100 on the
            // bounds-derived canvas width/height bindings. Defer to the next
            // macrotask so the update lands in its own change-detection run.
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = setTimeout(() => this.resize());
        });
        this._resizeObserver.observe(this.host.nativeElement);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only a genuinely new Asset needs to show the spinner again and wait on a fresh <img> load - the
        // `loading` flag only ever clears via that element's 'load' event (ngAfterViewChecked), which won't
        // fire again just because `isActive`/`slot` flipped (e.g. activating the passive pane in compare
        // mode, §3.4) while `asset` itself is unchanged. Resetting `loading` on every input change left it
        // stuck true forever in that case, since nothing was left to clear it.
        const assetChange = changes.asset;
        if (!assetChange || assetChange.previousValue?.id === assetChange.currentValue?.id) {
            return;
        }

        this.loading = true;
        this.clear();
    }

    ngOnDestroy() {
        this._destroyed = true;
        this._resizeObserver.disconnect();
        clearTimeout(this._resizeTimeout);
    }

    ngAfterViewChecked() {
        if (this.ctx) {
            return;
        }

        if (!this.canvas) {
            return;
        }

        this.ctx = this.canvas.nativeElement.getContext('2d');
        const img = this.img.nativeElement as HTMLImageElement;

        fromEvent(img, 'load').subscribe(() => {
            this.resize();
            this.loading = false;
        });
    }

    // view is [zoom, panX, panY] (review-image.service.ts) - zoom relative to THIS pane's own fit-to-
    // window size, pan as a fraction of the resulting on-screen size. Reconstructing it against this.asset/
    // this.bounds (rather than denormalizing an absolute pane-pixel matrix) is what makes a locked mirror
    // from a differently-sized/shaped Asset in the other slot (§3.5) land at the equivalent zoom/pan here,
    // instead of reusing the other Asset's own pixel dimensions.
    public applyTransform(view: number[]): void {
        if (!this.ctx) {
            return;
        }

        const [zoom, panX, panY] = view;
        const fit = new Rect(0, 0, this.asset.width, this.asset.height).fit(this.bounds.width, this.bounds.height);
        const width = fit.width * zoom;
        const height = fit.height * zoom;
        const x = (this.bounds.width - width) / 2 + panX * width;
        const y = (this.bounds.height - height) / 2 + panY * height;

        this.xform = new Matrix([
            [width, 0, 0],
            [0, height, 0],
            [x, y, 1],
        ]);
        this.main = this.xform;
    }

    // -- Events
    @HostListener('window:mouseup')
    up() {
        this.isMouseDown = false;
        this.main = this.xform;
    }

    mouseDown(event: MouseEvent, presentingState: PresentingState): void {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        if (presentingState === PresentingState.Other) {
            return;
        }

        if (!this.isActive) {
            // The passive pane has no interactive surface (§3.4).
            return;
        }

        if (event.button === 0) {
            this.isMouseDown = true;
            this.origin.x = event.x;
            this.origin.y = event.y;
        }
    }

    @HostListener('window:mousemove', ['$event'])
    mouseMove(event: MouseEvent): void {
        if (this.isMouseDown) {
            if (event.movementX === 0 && event.movementY === 0) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            let x = event.clientX - this.origin.x;
            let y = event.clientY - this.origin.y;

            if (event.shiftKey) {
                if (this.asset.width / this.asset.height > 1.0) {
                    y = 0;
                } else {
                    x = 0;
                }
            }

            this.xform = Matrix.Identity().x(this.main);
            this.translate(x, y);
            this.notifyTransform();
        }
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            // User is editing a form control
            return;
        }

        let triggered = false;

        if (event.key === 'k' || event.code === 'Space') {
            this.scaleToWindow();
            triggered = true;
        }

        if (triggered) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    scaleToWindow() {
        if (this.presentingState === PresentingState.Other) {
            return;
        }

        if (!this.isActive) {
            return;
        }

        const size = this.xform.rect.fit(this.bounds.width, this.bounds.height);
        let scale = size.width / this.asset.width;
        scale = scale > 1.0 ? 1.0 : scale;
        this.center(scale);

        this.isScaledFit = true;
        this.emitTools();
    }

    center(scale = 1.0) {
        if (this.presentingState === PresentingState.Other) {
            this.applyTransform(this.lastView);
            return;
        }

        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);
        this.scale(scale, scale);
        const x: number = this.bounds.width / 2 - this.xform.elements[0][0] / 2;
        const y: number = this.bounds.height / 2 - this.xform.elements[1][1] / 2;
        this.translate(x, y);

        this.main = this.xform;
        this.notifyTransform();
    }

    zoom(event: WheelEvent, presentingState: PresentingState) {
        if (presentingState === PresentingState.Other) {
            return;
        }

        if (!this.isActive) {
            return;
        }

        const scale = 1.0 + (event.deltaY < 0 ? 0.1 : -0.1);
        const x = event.clientX - this.bounds.left;
        const y = event.clientY - this.bounds.top;
        this.xform = this.main.dup();
        this.translate(-x, -y);
        this.scale(scale, scale);
        this.translate(x, y);
        this.main = this.xform;
        this.notifyTransform();
    }

    clear() {
        if (this.ctx) {
            this.ctx.clearRect(0, 0, this.bounds.width, this.bounds.height);
        }
    }

    render() {
        if (this._destroyed) {
            return;
        }

        if (this.loading) {
            requestAnimationFrame(() => this.render());
            return;
        }

        this.clear();
        const rect = this.xform.rect;

        this.notifyBounds();

        this.ctx.drawImage(
            this.img.nativeElement,
            Math.floor(rect.x),
            Math.floor(rect.y),
            Math.floor(rect.width),
            Math.floor(rect.height),
        );

        requestAnimationFrame(() => this.render());
    }

    translate(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [x, y, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }

    scale(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [x, 0, 0],
            [0, y, 0],
            [0, 0, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();

        this.isScaledFit = false;
        this.emitTools();
    }

    public resize(): void {
        if (!this.asset) {
            return;
        }

        this.bounds = this.host.nativeElement.getBoundingClientRect() as Rect;
        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);

        const size = this.xform.rect.fit(this.bounds.width, this.bounds.height);
        const scale = size.width / this.asset.width;
        this.center(scale);
    }

    emitTools() {
        const viewerTools = [];
        if (this.isScaledFit) {
            viewerTools.push(this.tools.original);
        } else {
            viewerTools.push(this.tools.fitScreen);
        }

        // if (this.document.fullscreenElement === null) {
        //     viewerTools.splice(1, 0, this.tools.fullscreen);
        // }

        this.viewerToolsService.setTools(viewerTools);
    }

    private notifyBounds() {
        if (!this.isActive) {
            // The Stage overlay only ever tracks the active slot's Asset (§3.4) - a passive pane reporting
            // its own bounds would make Stage draw into the wrong pane.
            return;
        }

        const clientRect = Rect.fromDomRect(this.host.nativeElement.getBoundingClientRect());
        const bounds = this.xform.rect;
        const relativeBounds = this.xform.rect;
        bounds.x += clientRect.x;
        bounds.y += clientRect.y;

        const xformRect = this.xform.rect;
        const scale = this.asset.width / xformRect.width;
        const assetSize = new Size(this.asset.width, this.asset.height);

        this.reviewService.setAssetBounds({bounds, relativeBounds, scale, assetSize, assetId: this.asset.id, slot: this.slot});
    }

    private notifyTransform() {
        // Express the current on-screen matrix as [zoom, panX, panY] relative to THIS pane's own fit-to-
        // window size (the same Rect.fit() baseline resize()/center() use), not as raw pane-pixel values.
        // A raw pixel-matrix copy looks identical to both panes' renderers, but "identical pixels" isn't
        // "identical framing" once the two Assets have different dimensions - mirroring this asset-relative
        // view instead is what lets the other slot reconstruct the equivalent zoom/pan against its own,
        // possibly differently-sized, Asset (§3.5).
        const fit = new Rect(0, 0, this.asset.width, this.asset.height).fit(this.bounds.width, this.bounds.height);
        const width = this.xform.elements[0][0];
        const height = this.xform.elements[1][1];
        const zoom = width / fit.width;
        const centerX = (this.bounds.width - width) / 2;
        const centerY = (this.bounds.height - height) / 2;
        const panX = width === 0 ? 0 : (this.xform.elements[2][0] - centerX) / width;
        const panY = height === 0 ? 0 : (this.xform.elements[2][1] - centerY) / height;
        const view = [zoom, panX, panY];

        if (!this.isActive) {
            // Passive-pane state only ever broadcasts via lock-mirroring or becoming active itself (§3.4) -
            // its own local resize/center() recompute never syncs to other members. It still needs to reach
            // reviewImageService locally though: this pane's own `vm$` (filtered to this.slot) is what the
            // template's `@if (vm$ | async; as vm)` gates its <img>/<canvas> on, so the passive pane simply
            // never renders until something seeds transform$ for its slot - the first resize() every pane
            // gets on mount/compare-entry (review.component.ts's resizeHandler(), via IResizable) is that
            // seed.
            this.reviewImageService.setTransform(view, this.slot);
            return;
        }

        // The active pane already renders off its own locally-mutated this.xform (zoom/translate/center) -
        // it doesn't need reviewImageService to render itself, and must NOT round-trip through it here:
        // vm$'s tap calls applyTransform(), which sets this.main = this.xform as a side effect meant to
        // commit a drag only on mouseup. Doing that on every mousemove too would make the next drag step
        // compound onto an already-updated anchor instead of the drag's original start, doubling the pan.
        const data = {
            [this.slot === Slot.Compare ? 'CompareTransform' : 'Transform']: view,
        };
        this.reviewService.send(data, MessageType.ReviewState);
    }
}
